'use client';

import React, { createContext, useContext } from 'react';
import useBusinessUnits from '@/lib/hooks/useBusinessUnits';
import { mapStore } from '@/utils/mappers/map-store';
import { mapBusinessUnit } from '@/utils/mappers/map-business-unit';
import { ActiveBusinessUnitAndStore } from '@/sdk/composable-commerce-b2b/types/actions/BusinessUnitActions';
import { ContextShape } from './types';

const initialState = {} as ContextShape;
export const StoreAndBusinessUnitsContext = createContext(initialState);

// EWM-482 — a logged-in user has a single BU + single store, resolved
// server-side at login. There is no switcher anywhere, so this is a
// READ-ONLY accessor (the old selection state / localStorage / page-reload
// machinery is gone):
//   - businessUnit comes from the full `useBusinessUnits()` payload so the
//     credit-status route-guard + dashboard keep the complete fields
//     (associates / creditStatus / fisThreshold) — see map-business-unit.ts.
//   - store comes from the server-resolved `activeSelection.store`, which is
//     locale-correct when the BU spans AU + NZ; falls back to the BU's first
//     store.
const StoreAndBusinessUnitsProvider = ({
  activeSelection,
  children,
}: React.PropsWithChildren<{ activeSelection?: ActiveBusinessUnitAndStore }>) => {
  const { defaultBusinessUnit } = useBusinessUnits();

  const selectedBusinessUnit = defaultBusinessUnit ? mapBusinessUnit(defaultBusinessUnit) : undefined;

  const activeStore = activeSelection?.store ?? defaultBusinessUnit?.stores?.[0];
  const selectedStore = activeStore ? mapStore(activeStore) : undefined;

  return (
    <StoreAndBusinessUnitsContext.Provider value={{ selectedBusinessUnit, selectedStore }}>
      {children}
    </StoreAndBusinessUnitsContext.Provider>
  );
};
export default StoreAndBusinessUnitsProvider;
export const useStoreAndBusinessUnits = () => useContext(StoreAndBusinessUnitsContext);
